import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  CheckBadgeIcon,
  BriefcaseIcon,
  MapPinIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { CountryFlag } from "@/shared/ui";
import { getCountryName } from "@/shared/core";

interface LawyerCardProps {
  lawyer: {
    id: string;
    university: string;
    years_of_experience: number;
    practice_areas: string[];
    accept_clients: boolean;
    hourly_rate?: number;
    is_verified: boolean;
    plan_type?: string | null;
    profiles: {
      full_name: string;
      profile_image_url?: string;
      country: string;
    };
  };
  onClick?: () => void;
}

export const LawyerCard: React.FC<LawyerCardProps> = ({ lawyer, onClick }) => {
  const { t } = useTranslation();

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPracticeAreaName = (area: string) => {
    return t(`public.lawyerCard.practiceAreas.${area}`, area);
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border border-tiber-200 p-6 cursor-pointer hover:shadow-md transition-all"
    >
      <div className="flex items-start space-x-4 mb-4">
        {/* Avatar */}
        {lawyer.profiles.profile_image_url ? (
          <img
            src={lawyer.profiles.profile_image_url}
            alt={lawyer.profiles.full_name}
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <div className="h-16 w-16 rounded-full bg-tiber-100 flex items-center justify-center shrink-0">
            <span className="text-tiber-600 font-semibold text-lg">
              {getInitials(lawyer.profiles.full_name)}
            </span>
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-bold text-lg text-juridico truncate">
                  {lawyer.profiles.full_name}
                </h3>
                <div className="flex items-center gap-1 shrink-0">
                  {lawyer.plan_type === 'lawyer_professional_pro' && (
                    <span
                      className="px-1.5 py-0.5 text-xs font-bold bg-tiber-500 text-white rounded"
                      title={t('public.lawyerCard.proBadge', 'Pro')}
                    >
                      Pro
                    </span>
                  )}
                  {lawyer.is_verified && (
                    <CheckBadgeIcon
                      className="h-5 w-5 text-atlantis-600"
                      title={t('public.lawyerCard.verified')}
                    />
                  )}
                </div>
              </div>
              <p className="text-sm text-tiber-700">{lawyer.university}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 text-xs text-tiber-700 mt-2">
            <div className="flex items-center space-x-1">
              <BriefcaseIcon className="h-4 w-4" />
              <span>{lawyer.years_of_experience} {t('public.lawyerCard.years')}</span>
            </div>
            <div className="flex items-center space-x-1">
              <MapPinIcon className="h-4 w-4" />
              <CountryFlag
                countryCode={lawyer.profiles.country}
                size="1em"
                title={getCountryName(lawyer.profiles.country)}
              />
              <span>{getCountryName(lawyer.profiles.country)}</span>
            </div>
            {lawyer.hourly_rate && (
              <div className="flex items-center space-x-1">
                <CurrencyDollarIcon className="h-4 w-4" />
                <span>${lawyer.hourly_rate}/hr</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Practice Areas */}
      {lawyer.practice_areas && lawyer.practice_areas.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {lawyer.practice_areas.slice(0, 3).map((area) => (
            <span
              key={area}
              className="inline-block px-2 py-1 text-xs font-medium bg-tiber-50 text-tiber-700 rounded"
            >
              {getPracticeAreaName(area)}
            </span>
          ))}
          {lawyer.practice_areas.length > 3 && (
            <span className="inline-block px-2 py-1 text-xs font-medium bg-tiber-100 text-tiber-700 rounded">
              {t('public.lawyerCard.moreAreas', { count: lawyer.practice_areas.length - 3 })}
            </span>
          )}
        </div>
      )}

      {/* Status */}
      <div className="pt-3 border-t border-tiber-100">
        {lawyer.accept_clients ? (
          <span className="inline-flex items-center text-xs font-medium text-atlantis-600">
            <CheckBadgeIcon className="h-4 w-4 mr-1" />
            {t('public.lawyerCard.acceptingClients')}
          </span>
        ) : (
          <span className="inline-flex items-center text-xs font-medium text-titan">
            {t('public.lawyerCard.notAcceptingClients')}
          </span>
        )}
      </div>
    </motion.div>
  );
};
